import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — Like2Fit" },
      {
        name: "description",
        content:
          "The terms that govern your use of Like2Fit, an AI-powered fashion discovery and styling app from Ratecard Technologies Limited.",
      },
      { property: "og:title", content: "Terms of Service — Like2Fit" },
      {
        property: "og:description",
        content:
          "The terms that govern your use of Like2Fit, an AI-powered fashion discovery and styling app from Ratecard Technologies Limited.",
      },
      { property: "og:url", content: "https://like2fit.com/terms" },
    ],
    links: [
      { rel: "canonical", href: "https://like2fit.com/terms" },
    ],
  }),
  component: TermsPage,
});

const EMAIL = "support@like2fit.com";

function Section({
  id,
  n,
  title,
  children,
}: {
  id: string;
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-10">
      <h2 className="text-xl font-semibold">
        {n}. {title}
      </h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function TermsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-5 py-8">
        <Link
          to="/"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← Back
        </Link>

        <h1 className="text-3xl font-semibold mt-6">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Last updated June 28, 2026
        </p>
        <p className="text-xs text-muted-foreground mt-3 italic">
          These terms are a starting template — please have them reviewed by
          a qualified lawyer before going live.
        </p>

        <div className="mt-6 text-[15px] leading-relaxed">
          <p>
            These Terms of Service ("Terms") form a binding agreement between
            you and <strong>Ratecard Technologies Limited</strong> ("we",
            "us", "our"), a company registered in England and Wales, and
            govern your access to and use of the Like2Fit website at{" "}
            <a
              href="https://like2fit.com"
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              like2fit.com
            </a>{" "}
            and any related services (together, the "Services"). By creating
            an account or otherwise using the Services, you agree to these
            Terms. If you do not agree, do not use the Services.
          </p>
        </div>

        <Section id="section-1" n={1} title="Agreement to Terms">
          <p>
            By accessing or using the Services you confirm that you can form
            a binding contract with us, that you accept these Terms, and that
            you agree to comply with them. These Terms incorporate our{" "}
            <Link to="/privacy" className="underline">
              Privacy Policy
            </Link>{" "}
            by reference.
          </p>
        </Section>

        <Section id="section-2" n={2} title="Eligibility">
          <p>
            You must be at least 18 years old to use the Services.
          </p>
          <p>
            We do not verify your age at signup; eligibility is based on your
            self-declaration when you create an account and, where applicable,
            when you enable personalisation features. If we become aware that
            a user is under 18, we will terminate the account and delete the
            associated data.
          </p>
        </Section>

        <Section id="section-3" n={3} title="Description of the Services">
          <p>Like2Fit is an AI-powered fashion discovery and styling app. You can:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Upload outfit screenshots from social media to identify
              individual clothing items and discover similar products across
              hundreds of retailers at personalised budget tiers.
            </li>
            <li>
              Upload photos of clothing you already own to receive
              AI-generated outfit pairing suggestions for specific occasions.
            </li>
            <li>
              Generate AI fashion editorial images showing complete outfits
              on a personalised model based on physical characteristics you
              provide (height, body shape, skin tone, hair type).
            </li>
            <li>
              Optionally upload a selfie photograph so AI-generated lookbook
              models resemble you. This feature is off by default and requires
              your explicit in-app consent; see our{" "}
              <Link to="/privacy" className="underline">
                Privacy Policy
              </Link>{" "}
              for how selfies are processed and retained.
            </li>
            <li>Save outfits to your library and revisit them later.</li>
          </ul>
          <p>
            We may add, change, or remove features at any time. Some features
            may be limited, throttled, or offered only to signed-in users.
          </p>
        </Section>

        <Section id="section-4" n={4} title="Accounts">
          <p>
            To access some features you need to create an account. You may
            sign up using <strong>Apple Sign-In</strong>,{" "}
            <strong>Google Sign-In</strong>, or an{" "}
            <strong>email and password</strong>. If you register with email
            and password, you must confirm your email address via the
            verification link we send before your account is activated.
          </p>
          <p>
            You agree to provide accurate, current information; keep your
            credentials secure; and accept responsibility for all activity
            under your account. Sign-in providers (Apple, Google) are
            subject to their own terms of service in addition to these
            Terms. Notify us promptly at{" "}
            <a href={`mailto:${EMAIL}`} className="underline">
              {EMAIL}
            </a>{" "}
            if you suspect unauthorised access.
          </p>
        </Section>

        <Section id="section-5" n={5} title="User Content and Licence">
          <p>
            "User Content" means any image, text, preference, or other
            material you upload or submit to the Services (including outfit
            screenshots, photos of garments, and physical characteristics you
            enter to generate lookbook models).
          </p>
          <p>
            You retain all rights you already hold in your User Content. By
            submitting it, you grant Ratecard Technologies Limited a
            worldwide, non-exclusive, royalty-free licence to host, store,
            process, transform, and display the User Content solely to
            operate and improve the Services for you — including running it
            through third-party AI providers we use to deliver features
            (image analysis, outfit pairing, lookbook generation).
          </p>
          <p>
            You warrant that you own or have the necessary rights to all
            User Content you upload, that it does not infringe any third
            party's intellectual property or privacy rights, and that you
            have any required consents from people depicted in it.
          </p>
          <p>
            <strong>Selfie photographs and your facial likeness.</strong> If
            you enable personalisation and upload a selfie, you additionally
            grant us a licence to transmit that selfie to our AI image
            provider (currently OpenAI) as a reference image for every
            lookbook render you generate, and to display the resulting
            personalised images to you within the Services. You confirm that
            the selfie is of yourself, that you are 18 or older, and that you
            consent to your facial likeness being used to generate AI imagery
            of you wearing the described outfits. You can withdraw this
            consent at any time by turning personalisation off or deleting
            your selfie in the Account tab.
          </p>
        </Section>

        <Section id="section-6" n={6} title="Acceptable Use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Upload images of other people without their consent, or any
              imagery of minors in a way that could harm them.
            </li>
            <li>
              Upload anything illegal, abusive, hateful, sexually explicit,
              or that depicts nudity or child sexual abuse material.
            </li>
            <li>
              Use the Services to harass, defame, or impersonate any person
              or brand.
            </li>
            <li>
              Reverse engineer, scrape, decompile, or attempt to extract the
              source code, training data, or model weights behind the
              Services, except where applicable law permits.
            </li>
            <li>
              Interfere with, overload, or attempt to gain unauthorised
              access to any part of the Services or their infrastructure.
            </li>
            <li>Use the Services to build a competing product.</li>
          </ul>
        </Section>

        <Section id="section-7" n={7} title="AI-Generated Content">
          <p>
            The Services use generative AI models. Outputs (including
            identified items, recommended products, prices, sizes, and
            editorial lookbook images) are probabilistic and may be
            inaccurate, incomplete, or out of date. They are not professional
            styling, sizing, or purchasing advice.
          </p>
          <p>
            AI outputs may resemble real garments, brands, or people without
            implying endorsement, affiliation, or availability. You are
            responsible for how you use AI outputs, including any decision
            to purchase a product or share an image.
          </p>
        </Section>

        <Section
          id="section-8"
          n={8}
          title="Third-Party Links and Affiliate Disclosure"
        >
          <p>
            The Services link out to third-party retailers. Some of those
            links are affiliate links, which means we may earn a commission
            when you click through and make a purchase, at no extra cost to
            you. We do not control third-party sites and are not responsible
            for their content, prices, availability, payment processing,
            shipping, returns, or privacy practices. Any transaction is
            solely between you and the third party.
          </p>
          <p>
            The Services also rely on the following third-party providers
            for core functionality: <strong>OpenAI</strong> (image
            generation and analysis), <strong>SerpApi</strong> (product
            search), <strong>Skimlinks</strong> (affiliate link
            attribution), and <strong>Resend</strong> (transactional
            email). Your use of features that depend on these providers is
            also subject to their terms and privacy practices, as
            summarised in our{" "}
            <Link to="/privacy" className="underline">
              Privacy Policy
            </Link>
            .
          </p>
        </Section>

        <Section id="section-9" n={9} title="Intellectual Property">
          <p>
            The Services, the Like2Fit name and logo, the underlying
            software, the lookbook model templates, and all related content
            we provide are owned by Ratecard Technologies Limited or our
            licensors and are protected by intellectual property laws. We
            grant you a limited, personal, non-transferable, revocable
            licence to use the Services for your own non-commercial use,
            subject to these Terms.
          </p>
        </Section>

        <Section id="section-10" n={10} title="Payments and Subscriptions">
          <p>
            The core Services are currently free to use. If we introduce
            paid plans in the future, we will update these Terms and present
            the applicable pricing, billing, and cancellation terms before
            you are charged.
          </p>
        </Section>

        <Section id="section-11" n={11} title="Termination">
          <p>
            You may delete your account at any time from the{" "}
            <strong>Account tab → Delete Account</strong> in the app, or by
            contacting us at{" "}
            <a href={`mailto:${EMAIL}`} className="underline">
              {EMAIL}
            </a>
            . On deletion your account is immediately soft-deleted and
            becomes inaccessible. You have a <strong>30-day recovery
            window</strong> during which you can email {EMAIL} from your
            account address to restore the account; after 30 days, all of
            your personal data is permanently erased as described in our{" "}
            <Link to="/privacy" className="underline">
              Privacy Policy
            </Link>
            .
          </p>
          <p>
            We may suspend or terminate your access to the Services at any
            time, with or without notice, if we believe you have breached
            these Terms or are misusing the Services. Accounts terminated
            for breach of these Terms forfeit the 30-day recovery window.
            Sections intended to survive (including User Content,
            Disclaimers, Limitation of Liability, Indemnification, and
            Governing Law) survive termination.
          </p>
        </Section>

        <Section id="section-12" n={12} title="Disclaimers">
          <p>
            To the maximum extent permitted by law, the Services are
            provided "as is" and "as available", without warranties of any
            kind, whether express or implied, including implied warranties
            of merchantability, fitness for a particular purpose, accuracy,
            or non-infringement. We do not warrant that the Services will
            be uninterrupted, error-free, or secure, or that AI outputs
            will be accurate, suitable, or available at any particular
            retailer or price.
          </p>
        </Section>

        <Section id="section-13" n={13} title="Limitation of Liability">
          <p>
            To the maximum extent permitted by law, Ratecard Technologies
            Limited and its officers, employees, and contractors will not
            be liable for any indirect, incidental, special, consequential,
            or punitive damages, or any loss of profits, revenue, data, or
            goodwill, arising out of or in connection with your use of the
            Services.
          </p>
          <p>
            Our total aggregate liability to you for any claim arising out
            of or relating to the Services or these Terms is limited to the
            greater of (a) the amount you paid us in the 12 months
            preceding the event giving rise to the claim, or (b) £100.
            Nothing in these Terms limits liability that cannot be limited
            under applicable law (such as for death or personal injury
            caused by negligence, or for fraud).
          </p>
        </Section>

        <Section id="section-14" n={14} title="Indemnification">
          <p>
            You agree to indemnify and hold harmless Ratecard Technologies
            Limited from any claims, damages, liabilities, and reasonable
            legal fees arising out of (a) your User Content, (b) your use
            of the Services in breach of these Terms, or (c) your violation
            of any law or third-party right.
          </p>
        </Section>

        <Section
          id="section-15"
          n={15}
          title="Governing Law and Jurisdiction"
        >
          <p>
            These Terms are governed by the laws of England and Wales. The
            courts of England and Wales have exclusive jurisdiction over
            any dispute arising out of or relating to these Terms or the
            Services, except that if you are a consumer resident in another
            country in the UK or EU, you may also bring proceedings in the
            courts of the country where you live.
          </p>
        </Section>

        <Section id="section-16" n={16} title="Changes to These Terms">
          <p>
            We may update these Terms from time to time. If the changes are
            material, we will give you reasonable notice (for example by
            email or in-app notice) before they take effect. Continuing to
            use the Services after the changes take effect means you accept
            the updated Terms.
          </p>
        </Section>

        <Section id="section-17" n={17} title="Contact">
          <p>
            Questions about these Terms? Contact us at{" "}
            <a href={`mailto:${EMAIL}`} className="underline">
              {EMAIL}
            </a>
            .
          </p>
        </Section>

        <section className="mt-14 border-t pt-6 text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-2">
          <span>
            Ratecard Technologies Limited —{" "}
            <a href={`mailto:${EMAIL}`} className="underline">
              {EMAIL}
            </a>
          </span>
          <Link to="/privacy" className="underline">
            Privacy Policy
          </Link>
          <Link to="/account-deletion" className="underline">
            Delete your account
          </Link>
        </section>
      </div>
    </div>
  );
}
